import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Participant } from '../App';
import AnalyticsApiService from '../services/analyticsApi';
import { ProfileImageCache } from '../services/profileImageCache';
import { processParticipant, convertLegacyGcsUrlToProxy } from '../utils/profileImage';

interface ProfileBubbleProps {
  participant: Participant;
  position: {
    top: string;
    left: string;
    transform: string;
    zIndex: number;
    size: string;
  };
  isSelected: boolean;
  isAnySelected: boolean;
  onClick: () => void;
  conversationId: number;
}

interface ProfileData {
  id: string;
  name: string;
  profileImage?: string;
  phoneNumber: string;
  role?: string;
  lastActive?: Date;
  status?: 'online' | 'offline' | 'away';
}



export function ProfileBubble({ 
  participant, 
  position, 
  isSelected, 
  isAnySelected,
  onClick,
  conversationId,
}: ProfileBubbleProps) {
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Process participant data to ensure profile images use proxy URLs
  const processedParticipant = processParticipant(participant);

  // Load profile data from backend when component mounts
  useEffect(() => {
    const loadProfileData = async () => {
      setIsLoading(true);
      try {
        // Use the cache for profile images
        const apiProfileImage = await ProfileImageCache.getProfileImage(processedParticipant.id, conversationId);

        if (apiProfileImage) {
          // Ensure the cached image URL is also converted
          const convertedImageUrl = convertLegacyGcsUrlToProxy(apiProfileImage);
          setProfileImage(convertedImageUrl);
        }
        
        // Set profile data from processed participant info
        setProfileData({
          id: processedParticipant.id,
          name: processedParticipant.name,
          phoneNumber: processedParticipant.phoneNumber,
          role: processedParticipant.role,
        });
      } catch (error) {
        console.warn('Failed to load profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [participant.id, participant.name, participant.phoneNumber, participant.role, conversationId]);

  // Calculate scale based on selection state
  const getScale = () => {
    if (!isAnySelected) return 1; // No one selected, normal size
    if (isSelected) return 1.15; // Selected bubble zooms in
    return 0.85; // Unselected bubbles zoom out
  };

  // Calculate opacity based on selection state
  const getOpacity = () => {
    if (!isAnySelected) return 1; // No one selected, full opacity
    if (isSelected) return 1; // Selected bubble full opacity
    return 0.6; // Unselected bubbles fade out
  };

  // Get size classes for different bubble sizes
  const getSizePixels = (sizeClass: string): number => {
    if (sizeClass.includes('w-24')) return 96;
    if (sizeClass.includes('w-20')) return 80;
    if (sizeClass.includes('w-19')) return 76;
    if (sizeClass.includes('w-18')) return 72;
    if (sizeClass.includes('w-17')) return 68;
    if (sizeClass.includes('w-16')) return 64;
    if (sizeClass.includes('w-15')) return 60;
    if (sizeClass.includes('w-14')) return 56;
    if (sizeClass.includes('w-13')) return 52;
    return 48;
  };

  const sizePixels = getSizePixels(position.size);
  const fontSize = Math.max(8, Math.floor(sizePixels / 5));

  // Determine image source priority: API image > processed participant image > fallback
  const imageSource = profileImage || processedParticipant.profileImage;

  // Generate initials from processed participant name
  const initials = processedParticipant.name
    .split(' ')
    .map((word: string) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <motion.div
      className="absolute cursor-pointer"
      style={{ 
        top: position.top, 
        left: position.left, 
        transform: position.transform, 
        zIndex: position.zIndex,
        // Ensure bubble stays within container bounds
        contain: 'layout style',
      }}
      onClick={onClick}
      animate={{
        scale: getScale(),
        opacity: getOpacity(),
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      }}
      whileHover={{
        // More conservative hover scaling to prevent overflow
        scale: getScale() * 1.03,
        transition: { duration: 0.2 }
      }}
      whileTap={{
        scale: getScale() * 0.97,
        transition: { duration: 0.1 }
      }}
    >
      <div 
        className={`rounded-full overflow-hidden bg-white dark:bg-gray-800 transition-all duration-300 ${
          isSelected 
            ? 'shadow-xl shadow-blue-200/50 dark:shadow-blue-900/50 ring-3 ring-blue-400/60 dark:ring-blue-500/60' 
            : 'shadow-lg shadow-black/10 ring-3 ring-white dark:ring-gray-700'
        }`}
        style={{ 
          width: `${sizePixels}px`, 
          height: `${sizePixels}px` 
        }}
      >
        {/* Loading state */}
        {isLoading && (
          <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <div 
              className="border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"
              style={{ 
                width: `${Math.max(12, sizePixels / 6)}px`, 
                height: `${Math.max(12, sizePixels / 6)}px` 
              }}
            />
          </div>
        )}

        {/* Profile image or fallback */}
        {!isLoading && (
          <>
            {imageSource && !imageError ? (
              <ImageWithFallback
                src={imageSource}
                alt={processedParticipant.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-300/90 to-gray-400/90 dark:from-gray-600/90 dark:to-gray-700/90 flex items-center justify-center">
                <span 
                  className="text-gray-700 dark:text-gray-300 font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[600] tracking-[-0.1px]"
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {initials}
                </span>
              </div>
            )}
          </>
        )}

        {/* Status indicator for online/offline */}
        {profileData?.status === 'online' && !isLoading && (
          <div 
            className="absolute bottom-0 right-0 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"
            style={{ 
              width: `${Math.max(8, sizePixels / 8)}px`, 
              height: `${Math.max(8, sizePixels / 8)}px` 
            }}
          />
        )}
      </div>

      {/* Hover tooltip with participant info */}
      <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="bg-black/80 dark:bg-gray-800/90 text-white dark:text-gray-100 text-xs rounded-lg px-2 py-1 whitespace-nowrap">
          {processedParticipant.name}
          {profileData?.status && (
            <span className={`ml-1 ${
              profileData.status === 'online' ? 'text-green-400' : 
              profileData.status === 'away' ? 'text-yellow-400' : 'text-gray-400 dark:text-gray-500'
            }`}>
              • {profileData.status}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}